import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateProfileData } from '../../redux/accountSlice';
import { Camera } from 'lucide-react';
import { optimizeProfileImage } from '../../utils/imageOptimizer';


const ProfileTab = ({ token }) => {
  const dispatch = useDispatch();
  const { profile, loading } = useSelector((state) => state.account);

  const [name, setName] = useState('');
  const [shopName, setShopName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  
  const [validationError, setValidationError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Load details from state
  useEffect(() => {
    if (profile) {
      setName(profile.name || '');
      setShopName(profile.shopName || '');
      setPhone(profile.phone || '');
      setEmail(profile.email || '');
      setAvatarPreview(profile.avatar || '');
    }
  }, [profile]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Basic image validation
      if (!file.type.startsWith('image/')) {
        setValidationError('Please select a valid image file (JPG, PNG, WEBP).');
        return;
      }
      try {
        const optimizedFile = await optimizeProfileImage(file);
        setAvatarFile(optimizedFile);
        setAvatarPreview(URL.createObjectURL(optimizedFile));
        setValidationError('');
      } catch (err) {
        console.error('Avatar optimization failed, using original file:', err);
        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
        setValidationError('');
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');
    setSuccessMessage('');

    if (!name.trim()) {
      setValidationError('Name is required.');
      return;
    }

    if (!shopName.trim()) {
      setValidationError('Shop name is required.');
      return;
    }

    const trimmedPhone = phone.trim();
    if (trimmedPhone) {
      const phoneRegex = /^\+?[0-9\s\-()]{7,20}$/;
      if (!phoneRegex.test(trimmedPhone)) {
        setValidationError('Invalid phone number format.');
        return;
      }
    }

    // Build Form Data for upload support
    const formData = new FormData();
    formData.append('name', name.trim());
    formData.append('shopName', shopName.trim());
    formData.append('phone', trimmedPhone);
    if (avatarFile) {
      formData.append('avatar', avatarFile);
    }

    try {
      await dispatch(updateProfileData({ formData, token })).unwrap();
      setSuccessMessage('Profile details updated successfully!');
      setAvatarFile(null);
      // Clean up notifications after 4 seconds
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      setValidationError(err || 'Failed to update profile.');
    }
  };

  // Generate initials for avatar fallback
  const getInitials = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <div style={{ maxWidth: '650px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      
      {/* Title */}
      <h2 style={{ color: 'var(--color-text-primary)', fontSize: '1.8rem', margin: 0 }}>Profile Information</h2>

      <form onSubmit={handleFormSubmit} style={{ marginTop: '10px' }}>
        
        {/* Messages */}
        {validationError && (
          <div style={{
            background: 'rgba(239,68,68,0.15)',
            color: '#fecaca',
            border: '1px solid rgba(248,113,113,0.3)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {validationError}
          </div>
        )}

        {successMessage && (
          <div style={{
            background: 'rgba(34,197,94,0.15)',
            color: '#bef264',
            border: '1px solid rgba(134,239,172,0.3)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {successMessage}
          </div>
        )}

        {/* Avatar Upload Block */}
        <div className="profile-avatar-upload-block">
          <div className="profile-avatar-edit-box">
            {avatarPreview ? (
              <img src={avatarPreview} alt={name} />
            ) : (
              <span>{getInitials(name) || 'U'}</span>
            )}
          </div>
          <div className="profile-avatar-upload-actions">
            <input
              type="file"
              id="avatar-upload-input"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="avatar-upload-input">
              <Camera size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              Upload Image
            </label>
            <span>Allowed formats: JPG, JPEG, PNG, WEBP.</span>
          </div>
        </div>

        {/* Inputs */}
        <div className="form-group-item">
          <label>Email Address (read-only)</label>
          <input
            type="email"
            value={email}
            disabled
            style={{ color: 'var(--color-text-muted)' }}
          />
        </div>

        <div className="form-group-item">
          <label>Full Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter full name"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group-item">
          <label>Shop / Company Name *</label>
          <input
            type="text"
            value={shopName}
            onChange={(e) => setShopName(e.target.value)}
            placeholder="Enter shop name"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group-item">
          <label>Phone Number</label>
          <input
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="e.g. +91 9876543210"
            disabled={loading}
          />
        </div>

        <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-start' }}>
          <button type="submit" className="btn" disabled={loading} style={{ padding: '12px 36px' }}>
            {loading ? 'Saving Changes...' : 'Save Profile Details'}
          </button>
        </div>

      </form>

    </div>
  );
};

export default ProfileTab;
